import {
  HStack, VStack, Text, Badge, Checkbox, Tooltip, Box,
} from "@chakra-ui/react";
import { Task, TaskStatus } from "features/Resources/Task/models";
import { NewHireStageBadge } from "features/Resources/Task/Components/StageBadge";
import { TASK_STATUSES, CLOSED_TASK_STATUSES } from "features/Resources/Task/constants";
import { TaskActionRowButton } from "features/Resources/Task/Components/Action/Button";
import DueDateTextWithColor from "features/Resources/Task/Components/DueDateTextWithColor";
import moment from "moment";

interface TaskItemProps {
  id: string;
  title: string;
  description?: string;
  dueDate: string;
  dueDateTooltip?: string;
  priority: "high" | "medium" | "low";
  status: TaskStatus;
  category: string;
  onToggle: (taskId: string) => void;
  showCategory?: boolean;
  showDescription?: boolean;
  isUrgent?: boolean;
  isOverdue?: boolean;
  startedAt?: Date | string;
  task?: Task;
  onClick?: () => void;
}

export default function TaskItem({
  id,
  title,
  description,
  dueDate,
  dueDateTooltip,
  priority,
  status,
  category,
  onToggle,
  showCategory = true,
  showDescription = false,
  isUrgent = false,
  isOverdue = false,
  startedAt,
  task,
  onClick,
}: TaskItemProps) {
  const isCompleted = status === "completed";
  const isClosed = CLOSED_TASK_STATUSES.includes(status);
  const showLeftBorder = isUrgent || isOverdue || isCompleted;

  // Check if task was started within last 48 hours
  const getRecentlyAddedLabel = () => {
    if (!startedAt) return null;

    const now = moment();
    const started = moment(startedAt);
    const hoursDiff = now.diff(started, "hours");

    if (hoursDiff < 0) return null; // Future date
    if (hoursDiff > 48) return null; // More than 48 hours ago

    const daysDiff = now.diff(started, "days");
    if (daysDiff === 0) return "Added Today";
    if (daysDiff === 1) return "Added Yesterday";

    return null;
  };

  const recentlyAddedLabel = getRecentlyAddedLabel();

  // Determine left border color and badge
  const getLeftBorderColor = () => {
    if (isCompleted) return "cdsGreen.500";
    if (isOverdue) return "statusRed.text";
    if (isUrgent) return "cdsOrange.500";
    return "gray.300";
  };

  // Get status configuration from TASK_STATUSES
  const statusConfig = TASK_STATUSES.get(status) || { colors: "gray.500", label: status };

  // Status badge background colors
  const getStatusBadgeColors = (taskStatus: TaskStatus) => {
    switch (taskStatus) {
      case "active":
      case "in_progress":
        return { bg: "blue.100", color: "blue.800" };
      case "blocked":
        return { bg: "orange.100", color: "orange.800" };
      case "completed":
        return { bg: "green.100", color: "green.800" };
      case "failed":
        return { bg: "red.100", color: "red.800" };
      case "skipped":
      case "upcoming":
        return { bg: "gray.100", color: "gray.700" };
      default:
        return { bg: "gray.100", color: "gray.700" };
    }
  };

  const statusBadgeColors = getStatusBadgeColors(status);

  return (
    <Box
      position="relative"
      borderRadius="md"
      overflow="hidden"
      _hover={{ "& .task-content": { bg: "gray.100" } }}
    >
      {/* Left border for urgent/overdue/completed */}
      {showLeftBorder && (
        <Box
          position="absolute"
          left={0}
          top={0}
          bottom={0}
          width="4px"
          bg={getLeftBorderColor()}
        />
      )}

      <HStack
        className="task-content"
        pl={showLeftBorder ? 4 : 3}
        pr={3}
        py={3}
        bg="gray.50"
        transition="background 0.2s"
        align="start"
      >
        <HStack flex={1} spacing={3} align="start">
          <Box pt={0.5} onClick={(e) => e.stopPropagation()}>
            <Checkbox
              isChecked={isCompleted}
              onChange={() => onToggle(id)}
              colorScheme="green"
              isDisabled={isClosed && !isCompleted}
            />
          </Box>

          <VStack align="start" spacing={1} flex={1}>
            {/* Title */}
            <Text
              fontSize="sm"
              fontWeight="bold"
              color="content.primary"
              textDecoration={isClosed ? "line-through" : "none"}
              lineHeight="1.4"
              cursor="pointer"
              onClick={onClick}
              _hover={{ textDecoration: "underline" }}
            >
              {title}
            </Text>

            {/* Description */}
            {showDescription && description && (
              <Text fontSize="xs" color="content.tertiary" lineHeight="1.5">
                {description}
              </Text>
            )}

            {/* Stage and Status badges */}
            {showCategory && (
              <HStack spacing={2} mt={1}>
                {task?.taskType === "automated" && category && category !== "" && (
                  <NewHireStageBadge stageId={category} />
                )}

                {/* Status badge */}
                <Badge
                  fontSize="10px"
                  px={2}
                  py={0.5}
                  borderRadius="md"
                  bg={statusBadgeColors.bg}
                  color={statusBadgeColors.color}
                  textTransform="capitalize"
                  fontWeight="semibold"
                >
                  {statusConfig.label}
                </Badge>

                {/* Task action button */}
                {task && (
                  <Box onClick={(e) => e.stopPropagation()}>
                    <TaskActionRowButton task={task} />
                  </Box>
                )}
              </HStack>
            )}
          </VStack>
        </HStack>

        {/* Top right corner - Recently added and Due date */}
        <HStack align="start" spacing={2} flexShrink={0}>
          {/* Recently added badge */}
          {recentlyAddedLabel && (
            <Badge
              fontSize="10px"
              px={2}
              py={0.5}
              borderRadius="md"
              bg="gray.100"
              color="gray.700"
              textTransform="none"
              fontWeight="semibold"
            >
              {recentlyAddedLabel}
            </Badge>
          )}

          {/* Due date text - hide for completed/skipped/removed/closed_stale */}
          {task && !isClosed && (
            <Tooltip label={dueDateTooltip} isDisabled={!dueDateTooltip}>
              <Box>
                <DueDateTextWithColor task={task} fontSize="xs" whiteSpace="nowrap" />
              </Box>
            </Tooltip>
          )}
        </HStack>
      </HStack>
    </Box>
  );
}
